import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { SelectionId, SelectionIdSchema, SelectionParticipantIdSchema, SelectionSequenceIdSchema, UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithLook, UserDenormalizedWithoutLook } from "./user"
import { AddSelectionSequenceAppealRequest, SelectionParticipantDocumentMeta, SelectionParticipantNominateSelected } from "./selection-participant"
import { SpaceUserStatus } from "./space-user"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"

export const SequenceOptimisticLocking = z.object({
  id: SelectionSequenceIdSchema.or(z.literal("")),
})

export type SequenceOptimisticLocking = z.infer<typeof SequenceOptimisticLocking>

export const PostSelectionParticipantsRequest = z.object({
  image: z.boolean(),
  description: validateUserFreeInputText({ minLength: 1, maxLength: 1024 }).optional(),
})

export type PostSelectionParticipantsRequest = z.infer<typeof PostSelectionParticipantsRequest>

export const SelectionKind = z.union([
  z.literal("message"),
  z.literal("talk"),
])

export const PostSelectionsRequest = z.object({
  kind: SelectionKind.describe("投稿募集の種別。"),
  title: validateUserFreeInputText({ minLength: 1, maxLength: 30 }).optional().describe("投稿募集のタイトル。"),
})

export type PostSelectionsRequest = z.infer<typeof PostSelectionsRequest>

export const PutSelectionRequest = z.object({
  title: z.string().describe("投稿募集のタイトル。"),
})

export type PutSelectionRequest = z.infer<typeof PutSelectionRequest>

export const AddSelectionParticipateRequest = z.object({
  open: z.boolean(),
  sequence: SequenceOptimisticLocking,
})

export type AddSelectionParticipateRequest = z.infer<typeof AddSelectionParticipateRequest>

export const AddSelectionPseudoNominateRequest = z.object({
  count: z.number().min(1).max(100),
  sequence: SequenceOptimisticLocking,
})

export type AddSelectionPseudoNominateRequest = z.infer<typeof AddSelectionPseudoNominateRequest>

export const AddSelectionNominateRequest = z.object({
  count: z.number().min(1).max(100),
  sequence: SequenceOptimisticLocking,
})

export type AddSelectionNominateRequest = z.infer<typeof AddSelectionNominateRequest>

export const AddSelectionSequenceUpdateSelectedParticipantRequest = z.object({
  selected: z.boolean(),
  participantId: SelectionParticipantIdSchema,
  sequence: SequenceOptimisticLocking,
})

export type AddSelectionSequenceUpdateSelectedParticipantRequest = z.infer<typeof AddSelectionSequenceUpdateSelectedParticipantRequest>

export const AddSelectionSequenceRemoveNominatedParticipantRequest = z.object({
  participantId: SelectionParticipantIdSchema,
  sequence: SequenceOptimisticLocking,
})

export type AddSelectionSequenceRemoveNominatedParticipantRequest = z.infer<typeof AddSelectionSequenceRemoveNominatedParticipantRequest>

export const SelectionStatus = z.union([
  z.literal("published"),
  z.literal("finished"),
])

export type SelectionStatus = z.infer<typeof SelectionStatus>

export const SelectionDocumentMeta = z.intersection(
  PostSelectionsRequest,
  z.object({
    user_id: UserIdSchema,
    status: SelectionStatus,
  })
)

export type SelectionDocumentMeta = z.infer<typeof SelectionDocumentMeta>

export const SelectionDocumentPseudoNominate = z.object({
  kind: z.literal("type1"), // TODO
  value: z.array(z.object({
    id: SelectionParticipantIdSchema,
    user: UserDenormalizedWithoutLook,
  }))
})

export type SelectionDocumentPseudoNominate = z.infer<typeof SelectionDocumentPseudoNominate>

export const SelectionDocumentNominates = z.array(z.object({
  id: SelectionParticipantIdSchema,
  user: UserDenormalizedWithLook,
  selected: SelectionParticipantNominateSelected,
  appeal: AddSelectionSequenceAppealRequest.optional(),
}))

export type SelectionDocumentNominates = z.infer<typeof SelectionDocumentNominates>

export const SelectionDocumentBody = z.intersection(
  SelectionDocumentMeta,
  z.object({
    counts: z.object({
      participants: z.number(),
    }),
    current_pseudo_nominate: SelectionDocumentPseudoNominate.optional(),
    current_nominates: SelectionDocumentNominates,
    current_displaying_participant: z.intersection(
      SelectionParticipantDocumentMeta,
      z.object({
        id: SelectionParticipantIdSchema
      })
    ).nullable(),
    participants_count_next_update_scheduled_at: z.number().nullable().optional().describe("応募者数の次回更新予定時刻 (UNIX Timestamp mills)。daemon が定期的にカウントを更新する。"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SelectionDocumentBody = z.infer<typeof SelectionDocumentBody>

export const SelectionDocument = z.intersection(
  SelectionDocumentBody,
  z.object({
    id: SelectionIdSchema,
  })
)

export type SelectionDocument = z.infer<typeof SelectionDocument>

export class SelectionDocuments {

  public static readonly MAX_COUNT = 10 // See: https://castify-inc.slack.com/archives/C0629MYQNU8/p1712312850699869?thread_ts=1712302752.938029&cid=C0629MYQNU8

  /** VIP候補として取得するパワーランキング上位件数 */
  public static readonly VIP_POOL_LIMIT = 1000
  /** ノミネート時のサンプリングシャード数 */
  public static readonly NOMINATE_SAMPLE_SHARD_COUNT = 2
  /** 疑似ノミネート時のサンプリングシャード数 */
  public static readonly PSEUDO_NOMINATE_SAMPLE_SHARD_COUNT = 2
  /** ノミネート時のキャンセル穴埋め用の補欠人数 */
  public static readonly NOMINATE_ALTERNATE_COUNT = 3

  static toDocumentData(space: SelectionDocument): object {
    return {
      ...space,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SelectionDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SelectionId(doc.id),
      kind: data["kind"],
      title: data["title"],
      status: data["status"],
      user_id: data["user_id"],
      counts: data["counts"],
      current_pseudo_nominate: data["current_pseudo_nominate"],
      current_nominates: data["current_nominates"],
      current_displaying_participant: data["current_displaying_participant"],
      participants_count_next_update_scheduled_at: data["participants_count_next_update_scheduled_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const SelectionDenormalized = z.intersection(SelectionDocumentMeta,
  z.object({
    id: SelectionIdSchema,
    selected_participant: z.intersection(SpaceUserStatus, z.object({
      id: SelectionParticipantIdSchema,
      user: UserDenormalizedWithLook,
    })).nullable().describe("当選中の応募情報。"),
    last_selected_participant: z.intersection(SpaceUserStatus, z.object({
      id: SelectionParticipantIdSchema,
      user: UserDenormalizedWithLook,
      selected_at: z.number().describe("当選した日時 (UNIX Timestamp mills)。"),
    })).nullable().describe("最後に当選した応募情報。"),
    created_at: CreatedAt,
  })
).describe("非正規化された投稿募集の情報。このデータの更新有無やタイミングは箇所によって異なります。")

export type SelectionDenormalized = z.infer<typeof SelectionDenormalized>
